import React, { useRef, useState, Suspense } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Points, PointMaterial, Preload } from "@react-three/drei";
import * as random from "maath/random/dist/maath-random.esm";
import styled from "styled-components";

// Styled component to wrap the Canvas element, making it fullscreen and positioned absolutely
const StyledCanvasWrapper = styled.div`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  left: 0;
  pointer-events: none;
  background: linear-gradient(
    38.73deg,
    rgba(204, 0, 187, 0.15) 0%,
    rgba(201, 32, 184, 0) 50%
  ),
  linear-gradient(
    141.27deg,
    rgba(0, 70, 209, 0) 50%,
    rgba(0, 70, 209, 0.15) 100%
  );
`;

// Component that creates and animates a group of stars
const StarGroup = ({ color, speed, angle = 0 }) => {
  const ref = useRef();
  const [sphere] = useState(() => random.inSphere(new Float32Array(1700), { radius: 1.2 }));

  useFrame((state, delta) => {
    if (!ref.current) return;

    // base rotation around x/y
    ref.current.rotation.x -= delta * speed;
    ref.current.rotation.y -= delta * speed;

    // apply angle-driven z rotation (angle expected 0..1 or radians)
    const targetZ = typeof angle === 'number' ? angle * Math.PI * 2 : 0;
    // smooth interpolation
    ref.current.rotation.z += (targetZ - ref.current.rotation.z) * 0.1;
  });

  return (
    <Points ref={ref} positions={sphere} stride={3} frustumCulled>
      <PointMaterial
        transparent
        color={color}
        size={0.003}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </Points>
  );
};

// Main component wrapping the Canvas with the styled wrapper
const StyledStarsCanvas = ({ isPaused = false, angleOfRotation = 0 }) => {
  const starSpeed = !isPaused ? 0 : 0.05;

  // normalize angleOfRotation (0-100) to 0-1 and clamp
  const normalizedAngle = Math.max(0, Math.min(1, angleOfRotation / 100));

  return (
    <StyledCanvasWrapper>
      <Canvas camera={{ position: [0, 0, 1] }}>
        <Suspense fallback={null}>
          {/* Group of stars with different colors and speeds; pass normalized angle to rotate z-axis */}
          <StarGroup color="#FF1493" speed={starSpeed} angle={normalizedAngle} />
          <StarGroup color="#00BFFF" speed={starSpeed} angle={normalizedAngle} />
          <StarGroup color="#FF4500" speed={starSpeed} angle={normalizedAngle} />
        </Suspense>
        <Preload all />
      </Canvas>
    </StyledCanvasWrapper>
  );
};

export default StyledStarsCanvas;
